import React, { useEffect, useState } from "react";
import {
  Chart,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Legend,
  Title,
} from "chart.js";
import { View, Image, Text, StyleSheet } from "@react-pdf/renderer";

if (typeof window !== "undefined") {
  Chart.register(
    RadarController,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Legend,
    Title
  );
}

const styles = StyleSheet.create({
  chartContainer: {
    justifyContent: "center",
    alignItems: "center",
    margin: 10,
  },
  errorContainer: {
    justifyContent: "center",
    alignItems: "center",
    padding: 10,
  },
  chartImage: {
    width: 400,
    height: 400,
  },
  loadingText: {
    fontSize: 12,
    color: "#888888",
  },
});

const generateRadarChart = () => {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      const canvas = document.createElement("canvas");
      canvas.width = 800;
      canvas.height = 800;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Could not get 2D context"));
        return;
      }

      try {
        const mainLabels = [
          "HEART\nCOLLABORATIVE",
          "SOUL\nVISIONARY", 
          "MIND\nSTRATEGIC", 
          "STRENGTH\nPERFORMATIVE"
        ];        

        const chartInstance = new Chart(ctx, {
          type: "radar",
          data: {
            labels: mainLabels,
            datasets: [
              {
                label: "LIFE",
                data: [80, 70, 60, 75],
                backgroundColor: "rgba(87, 149, 178, 0.5)", // Light blue transparency
                borderColor: "#245775",
                borderWidth: 2,
              },
              {
                label: "LEADERSHIP",
                data: [70, 80, 65, 60],
                backgroundColor: "rgba(201, 147, 20, 0.3)", // Golden transparency
                borderColor: "#C99314",
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: false,
            layout: {
              padding: 20, 
            },
            scales: {
              r: {
                beginAtZero: true,
                max: 100,
                ticks: {
                  display: false,
                  stepSize: 20,
                  font: {
                    size: 12,
                  },
                },
                pointLabels: {
                  callback: (value) => value.split("\n"),
                  font: {
                    size: 24,
                    weight: "bold",
                  },
                  color: (context) => {
                    const labelParts = mainLabels[context.index].split("\n");
                    return labelParts[0] === mainLabels[context.index].split("\n")[0] 
                      ? "#245775"  
                      : "#BF9540"; 
                  },
                  padding: 20, 
                },
              },
            },
            plugins: {
              legend: {
                position: "bottom",
                labels: {
                  usePointStyle: false,
                  boxWidth: 20,
                  borderWidth: 0,
                  font: {
                    size: 14,
                  },
                  plugins: {
                    legend: {
                      position: "bottom",
                      labels: {
                        usePointStyle: false,
                        boxWidth: 20,
                        color: "#333",
                        font: {
                          size: 14,
                        },
                        generateLabels: function (chart) {
                          return chart.data.datasets.map((dataset, i) => ({
                            text: dataset.label,
                            fillStyle: dataset.backgroundColor,
                            strokeStyle: "transparent",
                            lineWidth: 0,
                            pointStyle: "rect",
                          }));
                        },
                      },
                    },
                  },
                },
              },
              title: {
                display: true,
                font: {
                  size: 16,
                  weight: "bold",
                },
                color: "#333",
                padding: 15,
              },
            },
          },
        });

        // Convert to data URL
        setTimeout(() => {
          const dataUrl = canvas.toDataURL("image/png");
          chartInstance.destroy(); 
          resolve(dataUrl);
        }, 500);
      } catch (error) {
        reject(error);
      }
    } else {
      reject(new Error("Chart generation requires a browser environment"));
    }
  });
};

export default function AnalysisSecondChart() {
  const [chartImage, setChartImage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      generateRadarChart()
        .then(setChartImage)
        .catch((err) => {
          console.error("Chart generation error:", err);
          setError(err);
        });
    }
  }, []);

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text>Error generating chart: {error.message}</Text>
      </View>
    );
  }

  return (
    <View style={styles.chartContainer}>
      {chartImage ? (
        <Image src={chartImage} style={styles.chartImage} />
      ) : (
        <Text style={styles.loadingText}>Generating chart...</Text>
      )}
    </View>
  );
}